import classNames from 'classnames';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation, useRouteMatch } from 'react-router-dom';
import equal from 'fast-deep-equal';

import { UiButton } from 'src/components/UI/Button';
import { CSS_ANIMATION_DURATION } from 'src/config';
import { BrandingModelSelectorType } from 'src/models/Branding/BrandingModel';
import { SidebarMenuItemType, SidebarModelType } from 'src/models/Partner/Sidebar';
import {
  ROUTE_CAMPAIGN_AUTOAPPROVE,
  ROUTE_CAMPAIGN_DETAILS_PAGE,
  ROUTE_DONATION_PAGE_LIST,
  ROUTE_DONATION_PAGE_SETUP_PATH,
  ROUTE_EVERGREEN_UPGRADE,
  ROUTE_ORGANIZER_CAMPAIGNS,
  ROUTE_PEER_TO_PEER_REGISTRATION,
  ROUTE_PLUS_SUBSCRIPTION_UPGRADE,
  ROUTE_SIGNIN_PATH,
  ROUTE_START_GUIDE_ADVOCATE,
  ROUTE_START_GUIDE_CSR,
  ROUTE_START_GUIDE_EVERGREEN,
  ROUTE_START_GUIDE_ORGANIZATION,
  ROUTE_START_GUIDE_PERSONAL,
  ROUTE_START_GUIDE_PLUS_SUBSCRIPTION,
  ROUTE_STOP_PAGE,
} from 'src/routes';
import { commonSelector, leftMenuDataSelector } from 'src/store/commonPart/selector';
import { commonSlice } from 'src/store/commonPart/slice';
import { overlaySelector } from 'src/store/overlay/selectors';
import { getDOMElement } from 'src/utils/DOM';
import { BrandLogo } from 'src/components/Header/BrandLogo';
import { SidebarItemCategory } from 'src/components/Sidebar/ItemCategory';
import { useRefClickOutside } from 'src/utils/hooks/useRefClickOutside';
import { IconMenu } from 'src/components/Icons/Menu';
import { IconMenuClose } from 'src/components/Icons/MenuClose';
import { getSearchParams } from 'src/utils/url';
import { useBemHelper } from 'src/utils/hooks/useBemHelper';
import { MenuChevronLeftIcon } from 'src/components/Icons/Menu/ChevronLeft';
import {
  PlusSubscriptionIsFreeTrialAvailableSelector,
  PlusSubscriptionModalSelector,
  PlusSubscriptionPlanSelectionSelector,
} from 'src/store/plusSubscription/selectors';
import { SidebarOrganizationsList } from 'src/components/Sidebar/OrganizationsList';

declare global {
  interface Window {
    showSidebarMenu: (v: boolean) => void; // an outer api for managing sidebar
    safeShowSidebarMenu: {
      tempCalls?: boolean[]; // usually 1 value. maybe 2
      (v: boolean): void;
    };
  }
}

const togglerShowMaxWidth = 1490; // in pixels. Taken from .visible-less-1490 in _site-common.scss

const calculateSelectedItem = (pathname: string, items: Array<SidebarMenuItemType>): number | null => {
  /** ignore these routes */
  if (matchPath(pathname, [ROUTE_DONATION_PAGE_SETUP_PATH, ROUTE_CAMPAIGN_AUTOAPPROVE])) {
    return null;
  }

  /** the different way for campaign and donation setup pages */
  const filterFn: (item: SidebarMenuItemType) => boolean = pathname.startsWith('/campaigns/setup/') // new campaign url
    ? ({ url }) => pathname === url
    : ({ url }) => pathname.startsWith(url);

  return items.filter(filterFn).sort((i1, i2) => i2.url.length - i1.url.length)[0]?.id || null;
};

type Props = {
  brandingModel: BrandingModelSelectorType;
  authenticated: boolean;

  hideRallyUpLogo?: boolean;
};

let timer: number;

/**
 * true if left menu should be opened
 */
export const showLeftMenuOrNot = (pathname: string, innerWidth: number, overlayOpen: boolean): boolean => {
  /** if width is too small or there is campaign id in url, the menu must not be shown */
  if (innerWidth <= togglerShowMaxWidth || overlayOpen) {
    return false;
  }

  const lowerPathname = pathname.toLowerCase();

  /** closed */
  if (
    // setup pages always don't have menu opened
    ['/campaigns/setup', '/campaigns/donationpagesetup'].find((url) => lowerPathname.startsWith(url)) !== undefined ||
    [ROUTE_SIGNIN_PATH, ROUTE_PEER_TO_PEER_REGISTRATION].find((route) => matchPath(pathname, route)) ||
    pathname === '/'
  ) {
    return false;
  }

  /** opened */
  if (
    [
      '/campaigns/submit',
      '/campaigns/autoapprove',
      '/campaigns/publish',
      '/customertype',
      '/affiliatedashboard',
      '/evergreenorganizationsetup',
    ].find((url) => lowerPathname.startsWith(url)) !== undefined ||
    [
      ROUTE_START_GUIDE_ADVOCATE,
      ROUTE_START_GUIDE_ORGANIZATION,
      ROUTE_DONATION_PAGE_LIST,
      ROUTE_START_GUIDE_CSR,
      ROUTE_START_GUIDE_PERSONAL,
      ROUTE_START_GUIDE_EVERGREEN,
      ROUTE_START_GUIDE_PLUS_SUBSCRIPTION,
    ].find((route) => matchPath(pathname, route))
  ) {
    return true;
  }

  const fitsRoutesWithMenuClosed: boolean =
    [
      '/contributions',
      '/checkout',
      '/teammemberfullpage',
      '/me',
      '/teamfullpage',
      '/team',
      '/membercenter',
      '/home',
      '/organizationhomepage',
      '/startpage',
      '/registerorganizationpage',
      '/campaignpage',
      '/teammemberpage',
      '/teampage',
      '/payments/completepayment',
      '/administration',
      '/partner',
      '/paymentrecoverypage',
      '/account/profile/signup',
      '/account/profile/signuponboarding',
      '/donationpage',
      '/donpage',
      '/campaigns/',
      '/evergreenorganizationsetup',
      '/register',
    ].find((url) => lowerPathname.startsWith(url)) !== undefined;

  /**
   * routes on which the menu must be closed (true = closed)
   */
  let condition: boolean =
    fitsRoutesWithMenuClosed ||
    !!matchPath(pathname, ROUTE_CAMPAIGN_DETAILS_PAGE) ||
    !!matchPath(pathname, '/c/:id') ||
    !!matchPath(pathname, '/:id/m/:esheid/') ||
    !!matchPath(pathname, '/:id/t/:esheid/');
  /** get only campaign or donation public pages */
  if (
    !fitsRoutesWithMenuClosed &&
    (matchPath(pathname, { path: '/:id', exact: true }) ||
      matchPath(pathname, '/:id/campaign') ||
      matchPath(pathname, '/:id/teams') ||
      matchPath(pathname, '/:id/crowdfunding') ||
      matchPath(pathname, '/:id/event') ||
      matchPath(pathname, '/:id/sale') ||
      matchPath(pathname, '/:id/auction') ||
      matchPath(pathname, '/:id/pledge') ||
      matchPath(pathname, '/:id/members') ||
      matchPath(pathname, '/:id/sweepstakes') ||
      matchPath(pathname, '/:id/raffle'))
  ) {
    condition = true;
  }

  /** it's hard to calculate this route earlier */
  if (matchPath(pathname, ROUTE_ORGANIZER_CAMPAIGNS)) {
    condition = false;
  }

  return !condition;
};

export const Sidebar: React.FC<Props> = memo(
  ({ brandingModel, hideRallyUpLogo, authenticated }: Props) => {
    const dispatch = useDispatch();

    const { pathname } = useLocation();

    const leftMenuData = useSelector(leftMenuDataSelector);

    const { innerWidth, isCurrentPageSPA } = useSelector(commonSelector);

    const { show: overlayOpen } = useSelector(overlaySelector);

    const isRemovedCollapseModifierRef = useRef<boolean>(false);

    const bodyElement = useRef<HTMLElement>(getDOMElement('body'));
    const innerBodyElement = useRef<{ body: HTMLElement | null; bodyWrapper: HTMLElement | null }>({
      body: getDOMElement('#body'),
      bodyWrapper: getDOMElement('#r-body-wrapper'),
    });

    const [{ showMenu, initOpen, listenClickMenu, renderMenu }, setState] = useState<{
      showMenu: boolean;
      renderMenu: boolean;
      listenClickMenu: boolean;
      initOpen: boolean;
    }>({
      showMenu: false,
      renderMenu: false,
      listenClickMenu: false,
      initOpen: false,
    });

    /** managing opening the menu by external events */
    const { openedTestId } = getSearchParams(); // any url params on which menu must be toggled

    useEffect(() => {
      setState((v) => ({
        ...v,
        showMenu: !openedTestId,
      }));
    }, [openedTestId]);
    /** end managing opening the menu by external events */

    const changeState = useCallback(
      (
        data: Partial<{
          showMenu: boolean;
          renderMenu: boolean;
          listenClickMenu: boolean;
          initOpen: boolean;
        }>
      ) => {
        setState((v) => {
          isRemovedCollapseModifierRef.current = !v.listenClickMenu && !!data.listenClickMenu;

          return {
            ...v,
            ...data,
          };
        });
      },
      []
    );

    const bemHelper = useBemHelper('sidebar');

    const [opened, setOpened] = useState(true);
    const [hovered, setHovered] = useState(false);
    const [showOrganizationsList, setShowOrganizationsList] = useState(false);

    const MOBILE_WIDTH = 430; // latest iPhones
    const isMobile = innerWidth <= MOBILE_WIDTH;

    const toggleOrganizationsList = useCallback(() => {
      setShowOrganizationsList((showList) => !showList);

      if (isMobile) {
        setOpened((prevState) => !prevState);
      }
    }, [isMobile]);

    const sidebarModifiers = [
      !listenClickMenu && 'collapsed',
      showMenu && opened && 'opened',
      hovered && 'hovered',
    ].filter(Boolean) as string[];

    /** decide to show menu or not depending on pathname or screen dimensions */
    useEffect(() => {
      const open = showLeftMenuOrNot(pathname, innerWidth, overlayOpen || !!openedTestId);

      if (!open) {
        setOpened(true);
        setHovered(false);
        setShowOrganizationsList(false);
      }

      changeState({ showMenu: open, initOpen: open, listenClickMenu: !open });
    }, [innerWidth, pathname, overlayOpen, changeState, openedTestId]);

    const toggleMenu = useCallback(() => {
      if (authenticated) {
        setState((v) => ({
          ...v,
          showMenu: !v.showMenu,
        }));
      }

      isRemovedCollapseModifierRef.current = false;
    }, [authenticated]);

    /** set an unmount flag when menu is close */
    useEffect(() => {
      clearTimeout(timer);

      if (showMenu) {
        changeState({ renderMenu: true });
      } else {
        setHovered(false);
        timer = window.setTimeout(() => {
          changeState({ renderMenu: false });
        }, CSS_ANIMATION_DURATION);
      }

      return () => {
        clearTimeout(timer);
      };
    }, [changeState, showMenu]);

    const changeCurrentOrg = useCallback(
      (orgUrl: string) => {
        dispatch(commonSlice.actions.changeOrganizationStart(orgUrl));
      },
      [dispatch]
    );

    useEffect(() => {
      window.showSidebarMenu = (v: boolean) => {
        // #17230 this condition protects of opening menu on every click
        if (!listenClickMenu) {
          changeState({ showMenu: v });
        }
      };

      if (window?.safeShowSidebarMenu?.tempCalls) {
        window.safeShowSidebarMenu.tempCalls.forEach((v) => {
          setTimeout(() => {
            // it needs to be run just later
            window.showSidebarMenu(v);
          }, 0);
        });
        window.safeShowSidebarMenu.tempCalls = [];
      }
    }, [changeState, listenClickMenu, showMenu]);

    const close = useCallback(() => {
      changeState({ showMenu: false });
    }, [changeState]);

    const mobileOrganizationsListOpened = isMobile && showOrganizationsList;
    const ref = useRefClickOutside<HTMLDivElement>(
      listenClickMenu && renderMenu && !mobileOrganizationsListOpened,
      close
    );

    const hasMenuData = !!leftMenuData;

    /** set padding to the outer content */
    useEffect(() => {
      if (hasMenuData) {
        const className = 'padding-sidebar';

        if (initOpen) {
          if (showMenu) {
            innerBodyElement.current.body?.classList.add(className);
            innerBodyElement.current.bodyWrapper?.classList.add(className);
          } else {
            innerBodyElement.current.body?.classList.remove(className);
            innerBodyElement.current.bodyWrapper?.classList.remove(className);
          }
        } else if (getDOMElement('#body.padding-sidebar')) {
          innerBodyElement.current.body?.classList.remove(className);
          innerBodyElement.current.bodyWrapper?.classList.remove(className);
        }

        dispatch(commonSlice.actions.setSidebarIsOpen(showMenu));
      }
    }, [dispatch, initOpen, showMenu, hasMenuData]);

    const selectedItemId = useMemo(
      () =>
        leftMenuData?.categories
          ? calculateSelectedItem(
              pathname.toLowerCase(),
              leftMenuData.categories
                .flatMap((c) => c.items)
                // sub-items
                .flatMap((item) => [item, ...item.items])
                .map((item) => ({
                  ...item,
                  url: item.url ? item.url.toLowerCase() : '',
                }))
            )
          : null,
      [leftMenuData?.categories, pathname]
    );

    const btnMenuHidden = leftMenuData ? leftMenuData.categories.length === 0 || !listenClickMenu : true;

    const { IsFreeTrialExpired } = useSelector(PlusSubscriptionPlanSelectionSelector);
    const showFreeTrial = useSelector(PlusSubscriptionIsFreeTrialAvailableSelector);
    const { type: plusSubscriptionModalType } = useSelector(PlusSubscriptionModalSelector);

    const isPlusSubscriptionUpdatePage = useRouteMatch([ROUTE_EVERGREEN_UPGRADE, ROUTE_PLUS_SUBSCRIPTION_UPGRADE]);
    const isStopPage = useRouteMatch(ROUTE_STOP_PAGE);

    const disabledItems =
      IsFreeTrialExpired ||
      (!showFreeTrial && !isPlusSubscriptionUpdatePage && plusSubscriptionModalType === 'planSelection');

    const showSidebar = Boolean(authenticated && renderMenu && leftMenuData?.categories.length);

    /** disable/enable the window scrolling for small sizes */
    useEffect(() => {
      if (innerWidth > 1 && innerWidth < 1024) {
        bodyElement.current?.classList[showSidebar ? 'add' : 'remove']('bodyScrollLocked');
      } else if (!showSidebar && bodyElement.current?.classList.contains('bodyScrollLocked')) {
        bodyElement.current?.classList.remove('bodyScrollLocked');
      }
    }, [innerWidth, showSidebar]);

    const organizations = (leftMenuData as SidebarModelType | null)?.organizations;
    const organizationsExist = !!organizations?.length;

    if (isStopPage) {
      return (
        <div className="header-left-items verticalcenter">
          <BrandLogo brandingModel={brandingModel} hideRallyUpLogo={hideRallyUpLogo} />
        </div>
      );
    }

    return (
      <>
        <div className="header-left-items verticalcenter">
          <UiButton
            className={classNames('p-0 transparent sidebar-toggler no-box-shadow', {
              'visibility-hidden': btnMenuHidden,
            })}
            onClick={toggleMenu}
            dataTest="leftSideMenuIcon"
          >
            {showMenu ? <IconMenuClose /> : <IconMenu />}
          </UiButton>
          <BrandLogo brandingModel={brandingModel} hideRallyUpLogo={hideRallyUpLogo} />
        </div>

        {showSidebar && !isRemovedCollapseModifierRef.current && (
          <>
            <div className={bemHelper('backdrop')} />
            <div className={bemHelper(sidebarModifiers)} ref={ref}>
              <div className={bemHelper('header')}>
                <BrandLogo
                  brandingModel={brandingModel}
                  hideRallyUpLogo={hideRallyUpLogo}
                  className={bemHelper('logo')}
                  noClassImage
                />

                <UiButton className={bemHelper('icon-wrapper')} onClick={toggleMenu} dataTestId="toggleIconLeftMenu">
                  <MenuChevronLeftIcon className={bemHelper('icon')} />
                </UiButton>
              </div>

              {(leftMenuData as SidebarModelType).categories.map((category) => (
                <SidebarItemCategory
                  key={category.id}
                  category={category}
                  changeCurrentOrg={changeCurrentOrg}
                  selectedItemId={selectedItemId}
                  toggleOrganizationsList={toggleOrganizationsList}
                  organizationsExist={organizationsExist}
                  disabled={category.id !== 3 && disabledItems}
                  isCurrentPageSPA={isCurrentPageSPA}
                />
              ))}
            </div>

            {showOrganizationsList && organizationsExist && (
              <SidebarOrganizationsList
                organizations={organizations}
                toggleOrganizationsList={toggleOrganizationsList}
                changeCurrentOrg={changeCurrentOrg}
                isMobile={isMobile}
                setHovered={setHovered}
              />
            )}
          </>
        )}
      </>
    );
  },
  (prevProps, nextProps) => equal(prevProps, nextProps)
);
